import type { ReadPostgridContact } from '@/services/api/generated/postgridService';

// Local type definition
export type ContactType = 'individual' | 'company' | 'mixed';

/**
 * Determines the type of contact based on available name fields
 */
export const getContactType = (contact: ReadPostgridContact): ContactType => {
  const hasIndividualName = contact.first_name || contact.last_name;
  const hasCompanyName = contact.company_name;

  if (hasIndividualName && hasCompanyName) {
    return 'mixed' as ContactType;
  }
  if (hasCompanyName) {
    return 'company' as ContactType;
  }
  return 'individual' as ContactType;
};

// Re-export from ContactName for backwards compatibility
export { getContactDisplayName } from './ContactName';

/**
 * Gets the full formatted address string for a contact
 */
export const getContactFullAddress = (
  contact: ReadPostgridContact,
): string | null => {
  const parts = [
    contact.address_line1,
    contact.address_line2,
    contact.city,
    contact.province_or_state,
    contact.postal_or_zip,
    contact.country,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : null;
};

/**
 * Gets a shorter address string (typically without address_line2 and country)
 */
export const getContactShortAddress = (
  contact: ReadPostgridContact,
): string | null => {
  const parts = [
    contact.address_line1,
    contact.city,
    contact.province_or_state,
    contact.postal_or_zip,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : null;
};

/**
 * Gets the contact type badge color based on contact type
 */
export const getContactTypeBadgeColor = (
  contactType: ContactType,
): 'blue' | 'purple' | 'green' => {
  switch (contactType) {
    case 'company':
      return 'blue';
    case 'mixed':
      return 'purple';
    case 'individual':
    default:
      return 'green';
  }
};

/**
 * Gets the contact type label for display
 */
export const getContactTypeLabel = (contactType: ContactType): string => {
  switch (contactType) {
    case 'mixed':
      return 'Individual & Company';
    case 'company':
      return 'Company';
    case 'individual':
    default:
      return 'Individual';
  }
};
